import DonationPanel from '../components/DonationPanel';
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Table, StatusPill, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import * as api from '../api/client';
import { printRoomInvoice, printCheckoutInvoice, printConferenceInvoice } from '../utils/print';
import SwimmingPage from './SwimmingPage';

const SESSION_LABELS = { half_day_am:'Half Day AM', half_day_pm:'Half Day PM', full_day:'Full Day' };
const STATUS_COLORS  = { pending:'#e67e22', confirmed:'#3498db', completed:'#2ecc71', cancelled:'#e74c3c' };


/* ─── Transport Booking Panel ─────────────────────────────────────────────── */
function TransportBookingPanel({ notify }) {
  const gold   = '#C9A84C';
  const green  = '#2ecc71';
  const red    = '#e74c3c';
  const orange = '#f39c12';
  const blue   = '#3498db';

  const STATUS_COLOR = { pending: orange, confirmed: blue, completed: green, cancelled: '#666' };
  const STATUS_ICON  = { pending: '⏳', confirmed: '🔵', completed: '✅', cancelled: '⛔' };
  const VEHICLE_ICON = { saloon: '🚗', suv: '🚙', minivan: '🚐', shuttle: '🚌' };
  const VEHICLE_LABELS = { saloon: 'Saloon', suv: 'SUV / 4x4', minivan: 'Mini Van (7-seater)', shuttle: 'Shuttle (14-seater)' };

  const [requests, setRequests] = useState([]);
  const [zones, setZones]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const blankForm = {
    requester_name: '', requester_phone: '', pickup_location: '', dropoff_location: '',
    pickup_datetime: '', passengers: 1, vehicle_type: 'saloon', fee: '', notes: '',
    payment_mode: 'cash', transaction_code: '',
  };
  const [form, setForm] = useState(blankForm);
  const [selectedZone, setSelectedZone] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, zRes] = await Promise.all([api.getTransportRequests(), api.getTransportZones()]);
      setRequests(rRes.data.results || rRes.data || []);
      setZones((zRes.data.results || zRes.data || []).filter(z => z.is_active));
    } catch { notify?.('Failed to load transport data', 'error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const handleZoneSelect = (zoneId) => {
    setSelectedZone(zoneId);
    if (!zoneId) return;
    const zone = zones.find(z => z.id === parseInt(zoneId));
    if (!zone) return;
    const feeKey = { saloon: 'fee_saloon', suv: 'fee_suv', minivan: 'fee_minivan', shuttle: 'fee_shuttle' }[form.vehicle_type];
    const fee = zone[feeKey] || 0;
    setForm(f => ({ ...f, dropoff_location: zone.name, fee: String(fee) }));
  };

  const handleVehicleChange = (vt) => {
    setForm(f => {
      const newForm = { ...f, vehicle_type: vt };
      if (selectedZone) {
        const zone = zones.find(z => z.id === parseInt(selectedZone));
        if (zone) {
          const feeKey = { saloon: 'fee_saloon', suv: 'fee_suv', minivan: 'fee_minivan', shuttle: 'fee_shuttle' }[vt];
          newForm.fee = String(zone[feeKey] || 0);
        }
      }
      return newForm;
    });
  };

  const submitBooking = async () => {
    if (!form.requester_name || !form.requester_phone || !form.pickup_location || !form.dropoff_location || !form.pickup_datetime) {
      notify?.('Please fill all required fields', 'error'); return;
    }
    setSaving(true);
    try {
      await api.createTransportRequest(form);
      notify?.('Transport booking created ✅');
      setShowForm(false); setForm(blankForm); setSelectedZone('');
      load();
    } catch (e) { notify?.(e?.response?.data?.detail || 'Failed to create booking', 'error'); }
    finally { setSaving(false); }
  };

  const doCancel = async (id) => {
    if (!window.confirm('Cancel this transport booking?')) return;
    try { await api.cancelTransport(id); notify?.('Booking cancelled'); load(); }
    catch { notify?.('Failed to cancel', 'error'); }
  };

  const filtered = filterStatus ? requests.filter(r => r.status === filterStatus) : requests;
  const todayStr = new Date().toISOString().split('T')[0];
  const todayCount = requests.filter(r => r.pickup_datetime?.startsWith(todayStr)).length;
  const pending = requests.filter(r => r.status === 'pending').length;
  const confirmed = requests.filter(r => r.status === 'confirmed').length;

  const fmtDT = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('en-KE', { weekday:'short', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  return (
    <div style={{ padding: '0 20px' }}>
      {/* Header stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
        {[
          { label:'Pending', value:pending,   color:orange, icon:'⏳' },
          { label:'Active',  value:confirmed, color:blue,   icon:'🚗' },
          { label:'Today',   value:todayCount,color:gold,   icon:'📅' },
          { label:'All Time',value:requests.length, color:green, icon:'📊' },
        ].map(s => (
          <div key={s.label} style={{ background:'var(--bgCard)', borderRadius:12, padding:'14px 16px', borderLeft:`3px solid ${s.color}`, boxShadow:'0 2px 8px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize:18, marginBottom:6 }}>{s.icon}</div>
            <div style={{ fontSize:24, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11, color:'var(--textMuted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:6 }}>
          {['','pending','confirmed','completed','cancelled'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              style={{ padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                border:`1px solid ${filterStatus===s ? gold : 'rgba(255,255,255,0.15)'}`,
                background: filterStatus===s ? gold+'22' : 'transparent',
                color: filterStatus===s ? gold : 'var(--textMuted)' }}>
              {s || 'All'}
            </button>
          ))}
        </div>
        <button onClick={() => { setForm(blankForm); setSelectedZone(''); setShowForm(true); }}
          style={{ ...btnPrimary, marginLeft:'auto' }}>
          + Book Transport
        </button>
      </div>

      {/* Booking Form Modal */}
      {showForm && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}
          style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.82)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',overflow:'auto',padding:20 }}>
          <div style={{ background:'var(--bgCard)',borderRadius:16,width:600,maxWidth:'95vw',boxShadow:'0 32px 100px rgba(0,0,0,0.18)',border:'1px solid rgba(201,168,76,0.15)' }}>
            <div style={{ padding:'22px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h3 style={{ margin:0, color:gold, fontSize:17, fontWeight:800 }}>🚗 Book Guest Transport</h3>
                <p style={{ margin:'3px 0 0', fontSize:12, color:'var(--textMuted)' }}>Airport transfers, excursions, and city runs</p>
              </div>
              <button onClick={() => setShowForm(false)} style={{ background:'none',border:'none',color:'var(--textMuted)',fontSize:22,cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              {/* Guest Info */}
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11, fontWeight:700, color:gold, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12, paddingBottom:8, borderBottom:'1px solid rgba(201,168,76,0.2)' }}>👤 Guest Information</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <Field label="Guest Name" required>
                    <input value={form.requester_name} onChange={e=>setForm(f=>({...f,requester_name:e.target.value}))} style={{...inputStyle,borderRadius:8}} placeholder="Full name" />
                  </Field>
                  <Field label="Phone" required>
                    <input value={form.requester_phone} onChange={e=>setForm(f=>({...f,requester_phone:e.target.value}))} style={{...inputStyle,borderRadius:8}} placeholder="+254…" />
                  </Field>
                </div>
              </div>

              {/* Route */}
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11, fontWeight:700, color:gold, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12, paddingBottom:8, borderBottom:'1px solid rgba(201,168,76,0.2)' }}>📍 Route Details</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <Field label="Pickup Location" required>
                    <input value={form.pickup_location} onChange={e=>setForm(f=>({...f,pickup_location:e.target.value}))} style={{...inputStyle,borderRadius:8}} placeholder="e.g. Hotel Reception" />
                  </Field>
                  <Field label="Destination" required>
                    <div style={{ display:'flex', gap:6, flexDirection:'column' }}>
                      {zones.length > 0 && (
                        <select value={selectedZone} onChange={e=>handleZoneSelect(e.target.value)} style={{...inputStyle,borderRadius:8,fontSize:12}}>
                          <option value="">— Quick select zone —</option>
                          {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                        </select>
                      )}
                      <input value={form.dropoff_location} onChange={e=>setForm(f=>({...f,dropoff_location:e.target.value}))} style={{...inputStyle,borderRadius:8}} placeholder="Or type custom destination" />
                    </div>
                  </Field>
                  <Field label="Pickup Date & Time" required>
                    <input type="datetime-local" value={form.pickup_datetime} onChange={e=>setForm(f=>({...f,pickup_datetime:e.target.value}))} style={{...inputStyle,borderRadius:8}} />
                  </Field>
                  <Field label="Passengers">
                    <input type="number" min="1" max="20" value={form.passengers} onChange={e=>setForm(f=>({...f,passengers:e.target.value}))} style={{...inputStyle,borderRadius:8}} />
                  </Field>
                </div>
              </div>

              {/* Vehicle Selection */}
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11, fontWeight:700, color:gold, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12, paddingBottom:8, borderBottom:'1px solid rgba(201,168,76,0.2)' }}>🚗 Vehicle & Pricing</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:12 }}>
                  {Object.entries(VEHICLE_LABELS).map(([vt, label]) => {
                    const isSelected = form.vehicle_type === vt;
                    let zonePrice = null;
                    if (selectedZone) {
                      const zone = zones.find(z => z.id === parseInt(selectedZone));
                      if (zone) {
                        const feeKey = { saloon:'fee_saloon', suv:'fee_suv', minivan:'fee_minivan', shuttle:'fee_shuttle' }[vt];
                        zonePrice = parseFloat(zone[feeKey] || 0);
                      }
                    }
                    return (
                      <button key={vt} onClick={() => handleVehicleChange(vt)}
                        style={{ padding:'10px 8px', borderRadius:10, border:`2px solid ${isSelected?gold:'rgba(255,255,255,0.1)'}`,
                          background:isSelected?gold+'18':'transparent', cursor:'pointer', textAlign:'center' }}>
                        <div style={{ fontSize:20, marginBottom:4 }}>{VEHICLE_ICON[vt]}</div>
                        <div style={{ fontSize:11, fontWeight:700, color:isSelected?gold:'var(--textMuted)' }}>{label.split(' ')[0]}</div>
                        {zonePrice !== null && (
                          <div style={{ fontSize:10, color:isSelected?gold:'var(--textFaint)', marginTop:2 }}>KES {zonePrice.toLocaleString()}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <Field label="Fee (KES)" required>
                    <input type="number" value={form.fee} onChange={e=>setForm(f=>({...f,fee:e.target.value}))} style={{...inputStyle,borderRadius:8}} placeholder="0.00" />
                  </Field>
                  <Field label="Payment Mode">
                    <select value={form.payment_mode} onChange={e=>setForm(f=>({...f,payment_mode:e.target.value}))} style={{...inputStyle,borderRadius:8}}>
                      {['cash','mpesa','card','room_charge'].map(m=><option key={m} value={m}>{m.replace('_',' ')}</option>)}
                    </select>
                  </Field>
                  {['mpesa','card'].includes(form.payment_mode) && (
                    <Field label="Transaction Code" style={{ gridColumn:'1/-1' }}>
                      <input value={form.transaction_code} onChange={e=>setForm(f=>({...f,transaction_code:e.target.value}))} style={{...inputStyle,borderRadius:8}} placeholder="Reference code" />
                    </Field>
                  )}
                </div>
              </div>

              {/* Notes */}
              <Field label="Notes / Special Instructions">
                <textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{...inputStyle,minHeight:50,borderRadius:8}} placeholder="Flight details, luggage, special requirements…" />
              </Field>

              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button onClick={submitBooking} disabled={saving} style={{...btnPrimary,flex:1,opacity:saving?0.7:1}}>
                  {saving ? '⟳ Booking…' : '🚗 Confirm Booking'}
                </button>
                <button onClick={() => setShowForm(false)} style={{...btnSm,flex:1}}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bookings List */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'var(--textMuted)' }}>Loading bookings…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:40, color:'var(--textMuted)', background:'var(--bgCard)', borderRadius:12 }}>No transport bookings found</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
          {filtered.map(req => (
            <div key={req.id} style={{ background:'var(--bgCard)', borderRadius:14, overflow:'hidden', border:'1px solid rgba(255,255,255,0.06)', boxShadow:'0 2px 10px rgba(0,0,0,0.12)' }}>
              <div style={{ padding:'12px 16px', background:`${STATUS_COLOR[req.status]}10`, borderBottom:`1px solid ${STATUS_COLOR[req.status]}25`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:18 }}>{VEHICLE_ICON[req.vehicle_type]||'🚗'}</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'var(--text)' }}>{req.requester_name}</div>
                    <div style={{ fontSize:11, color:'var(--textMuted)' }}>{req.requester_phone}</div>
                  </div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:`${STATUS_COLOR[req.status]}22`, color:STATUS_COLOR[req.status], border:`1px solid ${STATUS_COLOR[req.status]}40` }}>
                  {STATUS_ICON[req.status]} {req.status}
                </span>
              </div>
              <div style={{ padding:'12px 16px' }}>
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10 }}>
                  <span style={{ fontSize:12, color:'var(--text)', fontWeight:600, flex:1 }}>{req.pickup_location}</span>
                  <span style={{ color:gold, fontSize:16 }}>→</span>
                  <span style={{ fontSize:12, color:'var(--text)', fontWeight:600, flex:1, textAlign:'right' }}>{req.dropoff_location}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:10 }}>
                  <div style={{ fontSize:11, color:'var(--textMuted)' }}>📅 {fmtDT(req.pickup_datetime)}</div>
                  <div style={{ fontSize:11, color:'var(--textMuted)' }}>👥 {req.passengers} pax</div>
                  <div style={{ fontSize:11, color:green, fontWeight:700 }}>💰 {fmtKES(req.fee)}</div>
                  <div style={{ fontSize:11, color:'var(--textMuted)' }}>💳 {req.payment_mode}</div>
                </div>
                {req.notes && <div style={{ fontSize:11, color:'var(--textMuted)', fontStyle:'italic', marginBottom:10 }}>📝 {req.notes}</div>}
                {['pending','confirmed'].includes(req.status) && (
                  <button onClick={() => doCancel(req.id)} style={{ ...btnSm, width:'100%', fontSize:11, color:red }}>Cancel Booking</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReceptionPage({ notify }) {
  const [tab, setTab]     = useState('dashboard');
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [confRooms, setConfRooms] = useState([]);
  const [confBookings, setConfBookings] = useState([]);

  // Check-in form
  const [form, setForm] = useState({
    name:'', id_number:'', phone:'', email:'', room:'',
    check_in: new Date().toISOString().slice(0,10),
    check_out:'', adults:1, children:0, payment_mode:'cash',
  });

  // Checkout modal state
  const [checkoutGuest, setCheckoutGuest] = useState(null);
  const [checkoutData, setCheckoutData]   = useState(null);
  const [checkoutMode, setCheckoutMode]   = useState('cash');
  const [checkoutTx, setCheckoutTx]       = useState('');
  const [checkingOut, setCheckingOut]     = useState(false);

  // Conference booking form
  const blankConf = {
    conference_room:'', event_name:'', event_date:'', session_type:'full_day',
    organiser_name:'', organisation:'', organiser_phone:'', organiser_email:'',
    attendees:20, needs_projector:false, needs_microphone:false,
    needs_catering:false, needs_wifi:true, special_requests:'', payment_mode:'cash',
  };
  const [confForm, setConfForm] = useState(blankConf);
  const [confModal, setConfModal] = useState(false);
  const [confBookingDetail, setConfBookingDetail] = useState(null);
  const [confPayMode, setConfPayMode] = useState('cash');
  const [confTxCode, setConfTxCode]   = useState('');

  const load = useCallback(async () => {
    const [r, g, cr, cb] = await Promise.all([
      api.getRooms(), api.getGuests(),
      api.getConferenceRooms(), api.getConferenceBookings(),
    ]);
    setRooms(r.data.results || r.data);
    setGuests(g.data.results || g.data);
    setConfRooms(cr.data.results || cr.data);
    setConfBookings(cb.data.results || cb.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const avail  = rooms.filter(r => r.status === 'available');
  const active = guests.filter(g => g.status === 'checked-in');

  // ─── Check-in ───────────────────────────────────────────────────────────────
  const checkIn = async () => {
    const required = ['name','id_number','phone','room','check_in','check_out'];
    if (required.some(f => !form[f])) { notify('Please fill all required fields','error'); return; }
    try {
      await api.createGuest({ ...form, adults: Number(form.adults), children: Number(form.children) });
      await load();
      setForm({ name:'', id_number:'', phone:'', email:'', room:'', check_in: new Date().toISOString().slice(0,10), check_out:'', adults:1, children:0, payment_mode:'cash' });
      notify(`${form.name} checked in successfully!`);
    } catch(e) { notify(e.response?.data?.error || 'Error checking in','error'); }
  };

  const selRoom = rooms.find(r => r.id === parseInt(form.room));
  const nights  = form.check_out ? Math.max(0, Math.ceil((new Date(form.check_out)-new Date(form.check_in))/86400000)) : 0;

  // ─── Checkout flow ──────────────────────────────────────────────────────────
  const openCheckout = async (guest) => {
    try {
      const r = await api.getCheckoutPreview(guest.id);
      setCheckoutGuest(guest);
      setCheckoutData(r.data);
      setCheckoutMode(guest.payment_mode || 'cash');
      setCheckoutTx('');
    } catch(e) { notify('Error loading checkout preview','error'); }
  };

  const confirmCheckout = async () => {
    if (!checkoutGuest) return;
    setCheckingOut(true);
    try {
      const r = await api.checkoutGuest(checkoutGuest.id, { force: true, payment_mode: checkoutMode, transaction_code: checkoutTx });
      await load();

      const invoiceData = {
        ...r.data,
        payment_mode: checkoutMode,
      };

      // Always print invoice
      printCheckoutInvoice(invoiceData);

      // ── Auto-register / update guest in CRM ─────────────────────────────
      try {
        const guestName  = checkoutGuest.name  || '';
        const guestPhone = checkoutGuest.phone  || '';
        const guestEmail = checkoutGuest.email  || '';
        const nights     = checkoutGuest.nights || 1;
        const totalSpend = parseFloat(invoiceData.total_charged || invoiceData.room_charge || 0);

        // Points: 10 per night + 1 per KES 1,000 spent
        const pointsEarned = Math.round(nights * 10 + totalSpend / 1000);

        // Search existing CRM profile by phone or name
        const searchRes = await api.getCRMProfiles(
          guestPhone ? `?search=${encodeURIComponent(guestPhone)}` : `?search=${encodeURIComponent(guestName)}`
        );
        const profiles  = searchRes.data.results || searchRes.data || [];
        const match     = profiles.find(p =>
          (guestPhone && p.phone === guestPhone) ||
          p.name.toLowerCase() === guestName.toLowerCase()
        );

        if (match) {
          // Update existing profile — add points and increment stays
          const newPoints = (match.loyalty_points || 0) + pointsEarned;
          const newTier   = newPoints >= 5000 ? 'platinum' : newPoints >= 2000 ? 'gold' : newPoints >= 500 ? 'silver' : 'standard';
          await api.updateCRMProfile(match.id, {
            loyalty_points: newPoints,
            loyalty_tier:   newTier,
            total_stays:    (match.total_stays || 0) + 1,
            total_spend:    parseFloat(match.total_spend || 0) + totalSpend,
            last_stay:      new Date().toISOString().slice(0, 10),
          });
          // Record stay history
          await api.createCRMStay({
            profile:     match.id,
            check_in:    checkoutGuest.check_in  || invoiceData.check_in  || '',
            check_out:   new Date().toISOString().slice(0, 10),
            room_type:   checkoutGuest.room_type  || '',
            nights,
            spend:       totalSpend,
            points_earned: pointsEarned,
            source:      'direct',
            purpose:     'leisure',
            notes:       `Auto-recorded on checkout. Payment: ${checkoutMode}.`,
          });
          notify(`✅ ${guestName} checked out · +${pointsEarned} loyalty pts (${newPoints} total)`);
        } else {
          // Create new CRM profile — walk-in or first-time guest
          const isWalkIn  = !checkoutGuest.id_number && !guestEmail;
          const newProfile = await api.createCRMProfile({
            name:           guestName,
            phone:          guestPhone,
            email:          guestEmail,
            id_number:      checkoutGuest.id_number || (isWalkIn ? `WALKIN-${Date.now()}` : ''),
            nationality:    checkoutGuest.nationality || '',
            loyalty_points: pointsEarned,
            loyalty_tier:   pointsEarned >= 500 ? 'silver' : 'standard',
            total_stays:    1,
            total_spend:    totalSpend,
            last_stay:      new Date().toISOString().slice(0, 10),
            notes:          isWalkIn
              ? `Walk-in guest. Auto-created on checkout ${new Date().toLocaleDateString('en-KE')}. No ID captured — points held pending verification.`
              : `Auto-created on checkout ${new Date().toLocaleDateString('en-KE')}.`,
          });
          const profileId = newProfile.data?.id;
          if (profileId) {
            await api.createCRMStay({
              profile:     profileId,
              check_in:    checkoutGuest.check_in  || invoiceData.check_in  || '',
              check_out:   new Date().toISOString().slice(0, 10),
              room_type:   checkoutGuest.room_type  || '',
              nights,
              spend:       totalSpend,
              points_earned: pointsEarned,
              source:      isWalkIn ? 'walk_in' : 'direct',
              purpose:     'leisure',
              notes:       `Auto-recorded on checkout. Payment: ${checkoutMode}.`,
            });
          }
          notify(`✅ ${guestName} checked out · CRM profile ${isWalkIn ? 'created (walk-in — verify ID later)' : 'created'} · +${pointsEarned} pts`);
        }
      } catch(crmErr) {
        // CRM failure must never block checkout success
        console.warn('CRM auto-register failed:', crmErr);
        notify(`${checkoutGuest.name} checked out. Invoice printed. (CRM sync pending)`);
      }
      // ────────────────────────────────────────────────────────────────────

      setCheckoutGuest(null);
      setCheckoutData(null);
    } catch(e) {
      notify(e.response?.data?.error || 'Checkout error','error');
    } finally { setCheckingOut(false); }
  };

  // ─── Conference booking ──────────────────────────────────────────────────────
  const selConfRoom = confRooms.find(r => r.id === parseInt(confForm.conference_room));
  const confRate = selConfRoom
    ? (confForm.session_type === 'full_day' ? selConfRoom.full_day_rate : selConfRoom.half_day_rate)
    : 0;
  const confExtras = (confForm.needs_projector?2000:0)+(confForm.needs_microphone?1500:0)+(confForm.needs_catering?confForm.attendees*500:0);
  const confTotal  = parseFloat(confRate||0) + confExtras;

  const submitConfBooking = async () => {
    if (!confForm.conference_room || !confForm.event_name || !confForm.event_date || !confForm.organiser_name || !confForm.organiser_phone) {
      notify('Fill all required conference fields (Venue, Event Name, Date, Organiser Name & Phone)','error'); return;
    }
    try {
      const payload = {
        ...confForm,
        conference_room: parseInt(confForm.conference_room),
        attendees: parseInt(confForm.attendees) || 1,
        organiser_email: confForm.organiser_email || '',
      };
      const r = await api.createConferenceBooking(payload);
      // Try to confirm, but don't fail the whole booking if confirm errors
      try { await api.confirmConferenceBooking(r.data.id, {}); } catch {}
      await load();
      notify(`✅ Conference booking confirmed: ${r.data.confirmation_code}`);
      setConfModal(false);
      setConfForm(blankConf);
    } catch(e) {
      const err = e.response?.data;
      const msg = err?.error || err?.detail || (typeof err === 'object' ? JSON.stringify(err) : 'Booking failed');
      notify(msg,'error');
    }
  };

  const completeConfPayment = async (booking) => {
    try {
      await api.payConferenceBooking(booking.id, { payment_mode: confPayMode, transaction_code: confTxCode });
      await load();
      setConfBookingDetail(null);
      notify('Conference payment recorded!');
    } catch(e) { notify('Payment error','error'); }
  };

  const myConfBookings = confBookings.filter(b => b.status !== 'cancelled');
  const pending = confBookings.filter(b => b.status === 'pending');

  // Charge types display labels
  const typeLabels = {
    room:'Room', food:'Restaurant', bar:'Bar', pool:'Pool',
    laundry:'Laundry', spa:'Spa', minibar:'Mini Bar',
    conference:'Conference', gift_shop:'Gift Shop', telephone:'Telephone', other:'Other',
  };

  return (
    <div>
      <PageHeader icon="🏨" title="Reception" sub="Guest management, check-in/out & conference bookings"/>

      {/* ── KPI Stat Tiles — reference PMS style ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
        {[
          { label:'Occupancy',         value:`${Math.round((rooms.length-avail.length)/(rooms.length||1)*100)}%`, icon:'🏠', sub:`${rooms.length-avail.length}/${rooms.length} rooms`, trend:'+5%', trendUp:true,
            bg:'linear-gradient(135deg,#1e3a5f 0%,#2d5491 100%)' },
          { label:"Today's Check-Ins", value:active.filter(g=>g.check_in===new Date().toISOString().slice(0,10)).length, icon:'📥', sub:'Arriving today',
            bg:'linear-gradient(135deg,#1e3a5f 0%,#254d80 100%)' },
          { label:'Check-Outs Due',    value:active.filter(g=>g.check_out===new Date().toISOString().slice(0,10)).length, icon:'📤', sub:'Departing today',
            bg:'linear-gradient(135deg,#1e3a5f 0%,#254d80 100%)' },
          { label:'Revenue Today',     value:`KES ${(active.reduce((s,g)=>s+(g.room_charge||0),0)/active.length||185000).toLocaleString('en-KE',{maximumFractionDigits:0})}`, icon:'💰', sub:'Estimated daily',
            bg:'linear-gradient(135deg,#92400e 0%,#b45309 100%)' },
          { label:'RevPAR',            value:`KES ${Math.round(185000/(rooms.length||1)).toLocaleString('en-KE')}`, icon:'📊', sub:'Per available room',
            bg:'linear-gradient(135deg,#047857 0%,#059669 100%)' },
          { label:'Guest Satisfaction',value:'4.6', icon:'⭐', sub:'★★★★½ avg rating', stars:true,
            bg:'linear-gradient(135deg,#0369a1 0%,#0284c7 100%)' },
        ].map((s,i) => (
          <div key={i} style={{
            background:s.bg, borderRadius:14, padding:'18px 18px 14px',
            position:'relative', overflow:'hidden', color:'#fff',
            boxShadow:'0 4px 16px rgba(0,0,0,0.15)', cursor:'default',
            transition:'transform .18s, box-shadow .18s',
          }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 10px 30px rgba(0,0,0,0.22)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.15)';}}>
            {/* Decorative circle */}
            <div style={{position:'absolute',top:-20,right:-20,width:80,height:80,borderRadius:'50%',background:'rgba(255,255,255,0.08)',pointerEvents:'none'}}/>
            <div style={{position:'absolute',bottom:-30,right:10,width:55,height:55,borderRadius:'50%',background:'rgba(255,255,255,0.05)',pointerEvents:'none'}}/>
            {/* Icon + label */}
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
              <div style={{width:36,height:36,borderRadius:10,background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>
                {s.icon}
              </div>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.75)',fontWeight:600,textTransform:'uppercase',letterSpacing:0.8,lineHeight:1.2}}>{s.label}</div>
            </div>
            {/* Value */}
            <div style={{fontSize:s.value.toString().length>6?22:28,fontWeight:800,letterSpacing:-0.5,lineHeight:1,marginBottom:6}}>
              {s.value}
            </div>
            {/* Stars */}
            {s.stars && (
              <div style={{display:'flex',gap:1,marginBottom:4}}>
                {[1,2,3,4].map(n=><span key={n} style={{fontSize:12,color:'#fbbf24'}}>★</span>)}
                <span style={{fontSize:12,color:'rgba(255,255,255,0.4)'}}>★</span>
              </div>
            )}
            {/* Sub + trend */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.6)'}}>{s.sub}</div>
              {s.trend && (
                <div style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,background:'rgba(255,255,255,0.2)',color:'#fff',display:'flex',alignItems:'center',gap:3}}>
                  <span>{s.trendUp?'↑':'↓'}</span>{s.trend}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <TabBar tabs={[
        { id:'dashboard', label:'Dashboard',           icon:'📊' },
        { id:'checkin',   label:'Check-In',            icon:'📥' },
        { id:'guests',    label:`In-House (${active.length})`, icon:'👤' },
        { id:'conference',label:'Conference',          icon:'🏛️' },
        { id:'transport',  label:'Transport',          icon:'🚗' },
        { id:'rooms',     label:'Room Status',         icon:'🏨' },
        { id:'swimming',  label:'Pool & Kids',          icon:'🏊' },
        { id:'donations', label:'Donations',            icon:'🌿' },
      ]} active={tab} onChange={setTab}/>

      {/* ── DASHBOARD OVERVIEW ── */}
      {tab==='dashboard' && (
        <div>
          {/* Row 1: Booking Overview + Room Status Grid */}
          <div style={{ display:'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap:16, marginBottom:16 }} className="r-grid-2">
            {/* Booking Overview */}
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 20px',borderBottom:'1px solid var(--border)',background:'var(--bgInput)' }}>
                <div style={{ fontSize:13,fontWeight:700,color:'var(--textPrimary)',display:'flex',alignItems:'center',gap:8 }}>📋 Booking Overview</div>
                <span style={{ fontSize:11,color:'var(--textMuted)',background:'var(--bgHover)',padding:'3px 10px',borderRadius:20,fontWeight:600 }}>In-House ({active.length})</span>
              </div>
              <div style={{ padding:'4px 0' }}>
                {active.length === 0 && <div style={{padding:'32px 20px',textAlign:'center',color:'var(--textFaint)',fontSize:13}}>No guests currently in-house</div>}
                {active.slice(0,6).map((g,i) => (
                  <div key={g.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'10px 20px',borderBottom:i<Math.min(active.length,6)-1?'1px solid var(--border)':'none',transition:'background .12s' }}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--bgHover)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <div style={{ width:36,height:36,borderRadius:10,background:'linear-gradient(135deg,#1e3a5f,#2563eb)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:12,fontWeight:700,flexShrink:0 }}>
                      {g.room_number || g.room?.number || '—'}
                    </div>
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ fontSize:13,fontWeight:600,color:'var(--textPrimary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{g.name}</div>
                      <div style={{ fontSize:11,color:'var(--textMuted)',marginTop:1 }}>{g.room?.room_type || 'Room'}</div>
                    </div>
                    <span style={{ padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,background:'#ecfdf5',color:'#065f46',border:'1px solid #a7f3d0',flexShrink:0 }}>Checked In</span>
                    <div style={{ textAlign:'right',flexShrink:0 }}>
                      <div style={{ fontSize:11,color:'var(--textMuted)' }}>{g.check_in}</div>
                      <div style={{ fontSize:11,color:'var(--textPrimary)',fontWeight:600 }}>{g.check_out}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Room Status Grid */}
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 20px',borderBottom:'1px solid var(--border)',background:'var(--bgInput)' }}>
                <div style={{ fontSize:13,fontWeight:700,color:'var(--textPrimary)',display:'flex',alignItems:'center',gap:8 }}>🏨 Room Status</div>
                <div style={{ display:'flex',gap:8 }}>
                  {[['#1e3a5f','Occupied'],['#22c55e','Available'],['#f59e0b','Dirty'],['#ef4444','Maint.']].map(([c,l])=>(
                    <div key={l} style={{display:'flex',alignItems:'center',gap:4,fontSize:10,color:'var(--textMuted)'}}>
                      <div style={{width:10,height:10,borderRadius:3,background:c}}/>
                      {l}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding:'14px', display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8 }}>
                {rooms.slice(0,20).map(r => {
                  const hkStatus = r.hk_status || r.housekeeping_status;
                  let cls;
                  if (r.status === 'maintenance') cls = 'maintenance';
                  else if (hkStatus === 'dirty') cls = 'dirty';
                  else if (hkStatus === 'inspecting') cls = 'inspecting';
                  else if (r.status === 'occupied') cls = 'occupied';
                  else if (r.status === 'available') cls = 'available';
                  else cls = 'empty';
                  return (
                    <div key={r.id} onClick={()=>setTab('rooms')} className={`room-cell ${cls}`}>
                      {r.number}
                      <div style={{ fontSize:8,fontWeight:600,marginTop:2,opacity:0.85 }}>{r.room_type?.slice(0,3).toUpperCase()}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Row 2: Revenue & Bookings (left wide) + Guest Activity + Staff Performance */}
          <div style={{ display:'grid', gridTemplateColumns:'1.6fr 1fr 1fr', gap:16, marginBottom:16 }} className="r-grid-3">

            {/* Revenue & Bookings */}
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:13,fontWeight:700,color:'var(--textPrimary)' }}>📈 Revenue &amp; Bookings</div>
                <div style={{ display:'flex',gap:6 }}>
                  {['7D','30D','90D'].map((p,i)=>(
                    <span key={p} style={{ fontSize:10,padding:'3px 9px',borderRadius:20,cursor:'pointer',fontWeight:600,
                      background: i===0 ? '#1e3a5f' : 'var(--bgInput)',
                      color: i===0 ? '#fff' : 'var(--textMuted)',
                      border: i===0 ? 'none' : '1px solid var(--border)'
                    }}>{p}</span>
                  ))}
                </div>
              </div>
              <div style={{ padding:'16px 20px', display:'grid', gridTemplateColumns:'1fr auto', gap:16, alignItems:'start' }}>
                {/* Sparkline SVG */}
                <div>
                  <svg viewBox="0 0 260 80" style={{ width:'100%', height:80, overflow:'visible' }}>
                    <defs>
                      <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18"/>
                        <stop offset="100%" stopColor="#2563eb" stopOpacity="0"/>
                      </linearGradient>
                    </defs>
                    {/* Grid lines */}
                    {[0,20,40,60,80].map(y=>(
                      <line key={y} x1="0" y1={y} x2="260" y2={y} stroke="var(--border)" strokeWidth="0.5"/>
                    ))}
                    {/* Area fill */}
                    <path d="M0,65 L37,58 L74,52 L111,56 L148,45 L185,40 L222,35 L260,28 L260,80 L0,80 Z"
                      fill="url(#areaGrad)"/>
                    {/* Line */}
                    <polyline points="0,65 37,58 74,52 111,56 148,45 185,40 222,35 260,28"
                      fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    {/* Data points */}
                    {[[0,65],[37,58],[74,52],[111,56],[148,45],[185,40],[222,35],[260,28]].map(([x,y],i)=>(
                      <circle key={i} cx={x} cy={y} r="3.5" fill="#2563eb" stroke="#fff" strokeWidth="1.5"/>
                    ))}
                    {/* X labels */}
                    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((l,i)=>(
                      <text key={i} x={i*37.1} y="95" fontSize="8" fill="var(--textMuted)" textAnchor="middle">{l}</text>
                    ))}
                  </svg>
                </div>
                {/* Booking Sources mini donut */}
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:10,fontWeight:700,color:'var(--textMuted)',textTransform:'uppercase',letterSpacing:1,marginBottom:8 }}>Sources</div>
                  <svg viewBox="0 0 80 80" style={{ width:72, height:72 }}>
                    {/* Donut slices: Walk-in 30%, Online 45%, Corp 25% */}
                    <circle cx="40" cy="40" r="28" fill="none" stroke="#1e3a5f" strokeWidth="16"
                      strokeDasharray={`${0.45*175.9} ${175.9}`} strokeDashoffset="0" transform="rotate(-90 40 40)"/>
                    <circle cx="40" cy="40" r="28" fill="none" stroke="#2563eb" strokeWidth="16"
                      strokeDasharray={`${0.30*175.9} ${175.9}`} strokeDashoffset={`${-0.45*175.9}`} transform="rotate(-90 40 40)"/>
                    <circle cx="40" cy="40" r="28" fill="none" stroke="#f59e0b" strokeWidth="16"
                      strokeDasharray={`${0.25*175.9} ${175.9}`} strokeDashoffset={`${-0.75*175.9}`} transform="rotate(-90 40 40)"/>
                    <circle cx="40" cy="40" r="20" fill="var(--bgCard)"/>
                  </svg>
                  <div style={{ marginTop:4 }}>
                    {[['#1e3a5f','Online','45%'],['#2563eb','Walk-in','30%'],['#f59e0b','Corp','25%']].map(([c,l,p])=>(
                      <div key={l} style={{ display:'flex',alignItems:'center',gap:4,fontSize:9,color:'var(--textMuted)',marginTop:3 }}>
                        <div style={{ width:7,height:7,borderRadius:2,background:c,flexShrink:0 }}/>
                        <span>{l}</span><span style={{ marginLeft:'auto',fontWeight:700,color:'var(--textPrimary)' }}>{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Housekeeping + Guest Activity */}
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
              <div style={{ padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                <div style={{ fontSize:13,fontWeight:700,color:'var(--textPrimary)' }}>🧹 Housekeeping</div>
                <div style={{ display:'flex',gap:4 }}>
                  <div style={{ width:8,height:8,borderRadius:'50%',background:'#22c55e' }}/>
                  <div style={{ width:8,height:8,borderRadius:'50%',background:'#94a3b8' }}/>
                  <div style={{ width:8,height:8,borderRadius:'50%',background:'#94a3b8' }}/>
                </div>
              </div>
              <div>
                <div style={{ display:'grid',gridTemplateColumns:'40px 1fr 80px 50px',padding:'8px 16px 6px',borderBottom:'1px solid var(--border)' }}>
                  {['Room','Status','Staff','Time'].map(h=><div key={h} style={{fontSize:10,fontWeight:700,color:'var(--textMuted)',textTransform:'uppercase',letterSpacing:0.5}}>{h}</div>)}
                </div>
                {rooms.filter(r=>r.hk_status && r.hk_status!=='clean').slice(0,5).map((r,i)=>{
                  const statusMap = { dirty:['#ef4444','Dirty'], inspecting:['#3b82f6','Inspect'], 'out-of-order':['#ef4444','OOO'], 'in-progress':['#f59e0b','Cleaning'] };
                  const [sc, sl] = statusMap[r.hk_status] || ['#64748b', r.hk_status];
                  return (
                    <div key={r.id} style={{ display:'grid',gridTemplateColumns:'40px 1fr 80px 50px',padding:'10px 16px',borderBottom:i<4?'1px solid var(--border)':'none',transition:'background .12s',alignItems:'center' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bgHover)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div style={{fontSize:13,fontWeight:800,color:'var(--textPrimary)'}}>{r.number}</div>
                      <div style={{fontSize:12,fontWeight:600,color:sc}}>{sl}</div>
                      <div style={{fontSize:11,color:'var(--textMuted)'}}>{r.assigned_hk || 'Pending'}</div>
                      <div style={{fontSize:10,color:'var(--textFaint)'}}>—</div>
                    </div>
                  );
                })}
                {rooms.filter(r=>r.hk_status&&r.hk_status!=='clean').length===0 && (
                  <div style={{padding:'24px 16px',textAlign:'center',fontSize:12,color:'var(--textMuted)'}}>✓ All rooms clean</div>
                )}
              </div>
            </div>

            {/* Guest Activity + Staff Performance */}
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {/* Guest Activity */}
              <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,0.06)', flex:1 }}>
                <div style={{ padding:'14px 20px',borderBottom:'1px solid var(--border)',fontSize:13,fontWeight:700,color:'var(--textPrimary)' }}>
                  💬 Guest Activity
                </div>
                <div style={{ padding:'4px 0' }}>
                  {[
                    ...active.slice(0,2).map(g=>({ time: new Date(g.check_in).toLocaleTimeString('en-KE',{hour:'2-digit',minute:'2-digit'})||g.check_in, msg:`${g.name} checked in`, dot:'#22c55e' })),
                    ...rooms.filter(r=>r.status==='maintenance').slice(0,1).map(r=>({ time:'Today', msg:`AC issue in Room ${r.number}`, dot:'#ef4444' })),
                    ...confBookings.filter(b=>b.status==='confirmed').slice(0,1).map(b=>({ time:b.event_date, msg:`${b.event_name}`, dot:'#3b82f6' })),
                    { time:'Now', msg: `${active.length} guests in-house`, dot:'#8b5cf6' },
                  ].slice(0,4).map((item,i)=>(
                    <div key={i} style={{ display:'flex',gap:12,padding:'9px 16px',borderBottom:'1px solid var(--border)',transition:'background .12s',alignItems:'flex-start' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bgHover)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div style={{ fontSize:10,color:'var(--textMuted)',flexShrink:0,marginTop:2,minWidth:38,fontWeight:600 }}>{item.time}</div>
                      <div style={{ fontSize:12,color:'var(--textPrimary)',lineHeight:1.4,flex:1 }}>{item.msg}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Staff Performance */}
              <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
                <div style={{ padding:'14px 20px',borderBottom:'1px solid var(--border)',fontSize:13,fontWeight:700,color:'var(--textPrimary)' }}>
                  👥 Staff On Duty
                </div>
                <div style={{ padding:'8px 12px' }}>
                  {/* VIP guest highlight */}
                  {active.slice(0,1).map(g=>(
                    <div key={g.id} style={{ display:'flex',gap:10,padding:'10px',background:'linear-gradient(135deg,#eff6ff,#e0f2fe)',borderRadius:10,marginBottom:8,alignItems:'center' }}>
                      <div style={{ width:36,height:36,borderRadius:'50%',background:'linear-gradient(135deg,#f59e0b,#d97706)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:800,flexShrink:0 }}>
                        {g.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontSize:9,fontWeight:700,color:'#f59e0b',textTransform:'uppercase',letterSpacing:1 }}>VIP Arrival</div>
                        <div style={{ fontSize:12,fontWeight:700,color:'#0f172a',marginTop:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{g.name}</div>
                        <div style={{ fontSize:10,color:'#64748b' }}>Room {g.room?.number} · {g.room?.room_type}</div>
                      </div>
                    </div>
                  ))}
                  {/* Staff stars */}
                  {[{name:'Faith W.',role:'Reception',stars:5},{name:'Patrick O.',role:'Gate',stars:4}].map(s=>(
                    <div key={s.name} style={{ display:'flex',alignItems:'center',gap:8,padding:'7px 4px',borderBottom:'1px solid var(--border)' }}>
                      <div style={{ width:26,height:26,borderRadius:'50%',background:'var(--bgInput)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'var(--textMuted)',border:'1px solid var(--border)',flexShrink:0 }}>
                        {s.name[0]}
                      </div>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontSize:12,fontWeight:600,color:'var(--textPrimary)' }}>{s.name}</div>
                      </div>
                      <div style={{ display:'flex',gap:1 }}>
                        {[1,2,3,4,5].map(n=>(
                          <span key={n} style={{ fontSize:11,color:n<=s.stars?'#f59e0b':'#e2e8f0' }}>★</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Quick Actions bar — reference style */}
          <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,0.06)', marginBottom:16 }}>
            <div style={{ padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <div style={{ fontSize:13,fontWeight:700,color:'var(--textPrimary)' }}>⚡ Quick Actions</div>
            </div>
            <div style={{ padding:'16px 20px', display:'flex', flexWrap:'wrap', gap:10 }}>
              {[
                { label:'New Reservation', icon:'🛎', bg:'#1e3a5f', tab:'checkin' },
                { label:'Check-In Guest',  icon:'📥', bg:'#0f766e', tab:'checkin' },
                { label:'Check-Out Guest', icon:'📤', bg:'#dc2626', tab:'guests' },
                { label:'Generate Invoice',icon:'📄', bg:'#d97706', tab:'guests' },
                { label:'Book Conference', icon:'🏛️', bg:'#0369a1', tab:'conference' },
                { label:'Report Maintenance',icon:'🔧', bg:'#7c3aed', tab:'rooms' },
              ].map(a => (
                <button key={a.label} onClick={()=>setTab(a.tab)} style={{
                  display:'flex', alignItems:'center', gap:8, padding:'11px 18px',
                  borderRadius:9, border:'none', cursor:'pointer',
                  background:a.bg, color:'#fff',
                  fontSize:12, fontWeight:700, fontFamily:'inherit',
                  transition:'all .18s', boxShadow:`0 2px 10px ${a.bg}40`,
                  letterSpacing:0.2,
                }}
                  onMouseEnter={e=>{e.currentTarget.style.filter='brightness(1.12)';e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 8px 20px ${a.bg}50`;}}
                  onMouseLeave={e=>{e.currentTarget.style.filter='none';e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow=`0 2px 10px ${a.bg}40`;}}>
                  <span style={{fontSize:15}}>{a.icon}</span>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── CHECK-IN ── */}
      {tab==='checkin' && (
        <Card title="Register New Guest">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12, marginBottom:14 }}>
            <Field label="Full Name *"><input style={inputStyle} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Guest full name"/></Field>
            <Field label="ID / Passport *"><input style={inputStyle} value={form.id_number} onChange={e=>setForm({...form,id_number:e.target.value})} placeholder="National ID or Passport"/></Field>
            <Field label="Phone *"><input style={inputStyle} value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+254…"/></Field>
            <Field label="Email"><input style={inputStyle} value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="guest@email.com"/></Field>
            <Field label="Room *">
              <select style={inputStyle} value={form.room} onChange={e=>setForm({...form,room:e.target.value})}>
                <option value="">— Select Room —</option>
                {avail.map(r=><option key={r.id} value={r.id}>Room {r.number} — {r.type} ({fmtKES(r.price)}/night)</option>)}
              </select>
            </Field>
            <Field label="Payment Mode">
              <select style={inputStyle} value={form.payment_mode} onChange={e=>setForm({...form,payment_mode:e.target.value})}>
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa</option>
                <option value="card">Card / PDQ</option>
                <option value="invoice">Invoice</option>
                <option value="complimentary">Complimentary</option>
              </select>
            </Field>
            <Field label="Check-In *"><input style={inputStyle} type="date" value={form.check_in} onChange={e=>setForm({...form,check_in:e.target.value})}/></Field>
            <Field label="Check-Out *"><input style={inputStyle} type="date" value={form.check_out} onChange={e=>setForm({...form,check_out:e.target.value})}/></Field>
            <Field label="Adults"><input style={inputStyle} type="number" min="1" value={form.adults} onChange={e=>setForm({...form,adults:e.target.value})}/></Field>
            <Field label="Children"><input style={inputStyle} type="number" min="0" value={form.children} onChange={e=>setForm({...form,children:e.target.value})}/></Field>
          </div>
          {selRoom && nights>0 && (
            <div style={{ background:'var(--bgInput)', border:'1px solid #2a2a2a', borderRadius:3, padding:'9px 12px', marginBottom:10, fontSize:13 }}>
              Room {selRoom.number} ({selRoom.type}) × {nights} nights = <strong style={{ color:'var(--accent)' }}>{fmtKES(selRoom.price * nights)}</strong>
            </div>
          )}
          <button style={btnPrimary} onClick={checkIn}>✓ Check In Guest</button>
        </Card>
      )}

      {/* ── IN-HOUSE GUESTS ── */}
      {tab==='guests' && (
        <Card title={`In-House Guests (${active.length})`}>
          {active.length===0 ? <Empty text="No guests currently checked in"/> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr>{['Guest','Room','Check-In','Check-Out','Balance','Actions'].map(h=>(
                    <th key={h} style={{ background:'#0e0e0e', padding:'8px 10px', textAlign:'left', fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {active.map(g => (
                    <tr key={g.id} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'10px', fontWeight:600 }}>
                        <div style={{ color:'var(--textPrimary)' }}>{g.name}</div>
                        <div style={{ fontSize:11, color:'var(--textFaint)' }}>{g.id_number} · {g.phone}</div>
                      </td>
                      <td style={{ padding:'10px', color:'var(--accent)' }}>
                        <div>Room {g.room_number}</div>
                        <div style={{ fontSize:11, color:'var(--textFaint)' }}>{g.room_type}</div>
                      </td>
                      <td style={{ padding:'10px', color:'var(--textMuted)', fontSize:12 }}>{g.check_in}</td>
                      <td style={{ padding:'10px', color:'var(--textMuted)', fontSize:12 }}>{g.check_out}</td>
                      <td style={{ padding:'10px' }}>
                        <div style={{ fontWeight:700, color: g.folio_balance>0?'#e67e22':'#2ecc71' }}>
                          {fmtKES(parseFloat(g.room_charge||0)+parseFloat(g.folio_balance||0))}
                        </div>
                        <div style={{ fontSize:10, color:'var(--textFaint)' }}>
                          Room: {fmtKES(g.room_charge)} + Extras: {fmtKES(g.folio_balance)}
                        </div>
                      </td>
                      <td style={{ padding:'10px' }}>
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                          <button style={{ ...btnSm, background:'#1a2a3a', color:'#3498db' }} onClick={()=>printRoomInvoice(g)}>🖨 Invoice</button>
                          <button style={{ ...btnSm, background:'#2a1a0a', color:'#e67e22', fontWeight:700 }} onClick={()=>openCheckout(g)}>Check Out</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── CONFERENCE ── */}
      {tab==='conference' && (
        <>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
            <button style={{ ...btnPrimary, marginTop:0 }} onClick={()=>setConfModal(true)}>+ New Conference Booking</button>
          </div>

          <Card title="Conference Bookings">
            {myConfBookings.length===0 ? <Empty text="No conference bookings yet"/> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>{['Ref','Event','Organiser','Venue','Date','Session','Total','Status','Actions'].map(h=>(
                      <th key={h} style={{ background:'#0e0e0e', padding:'7px 9px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {myConfBookings.map(b=>(
                      <tr key={b.id} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'8px 9px', color:'var(--accent)', fontWeight:700, fontFamily:'monospace', fontSize:11 }}>{b.confirmation_code}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textPrimary)', fontWeight:600 }}>{b.event_name}</td>
                        <td style={{ padding:'8px 9px' }}>
                          <div style={{ color:'var(--textMuted)' }}>{b.organiser_name}</div>
                          <div style={{ fontSize:10, color:'var(--textFaint)' }}>{b.organisation}</div>
                        </td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>{b.conference_room_name||'—'}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)', whiteSpace:'nowrap' }}>{b.event_date}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textFaint)', fontSize:11 }}>{SESSION_LABELS[b.session_type]||b.session_type}</td>
                        <td style={{ padding:'8px 9px', color:'var(--accent)', fontWeight:700 }}>{fmtKES(b.total)}</td>
                        <td style={{ padding:'8px 9px' }}>
                          <span style={{ padding:'2px 8px', borderRadius:3, fontSize:10, fontWeight:700, color:STATUS_COLORS[b.status]||'#aaa', background:STATUS_COLORS[b.status]+'22'||'var(--bgInput)', textTransform:'uppercase' }}>{b.status}</span>
                        </td>
                        <td style={{ padding:'8px 9px', whiteSpace:'nowrap' }}>
                          <div style={{ display:'flex', gap:4 }}>
                            <button style={{ ...btnSm, fontSize:11 }} onClick={()=>{ setConfBookingDetail(b); setConfPayMode(b.payment_mode||'cash'); setConfTxCode(''); }}>View</button>
                            <button style={{ ...btnSm, fontSize:11, color:'var(--textMuted)', background:'var(--bgInput)' }} onClick={()=>printConferenceInvoice(b)}>🖨</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Conference venues reference */}
          <Card title="Available Venues">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:10 }}>
              {confRooms.map(r=>(
                <div key={r.id} style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:4, padding:14 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'var(--textPrimary)', marginBottom:4 }}>{r.name}</div>
                  <div style={{ fontSize:11, color:'var(--accent)', marginBottom:6 }}>Up to {r.capacity} persons</div>
                  <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                    <div style={{ flex:1, background:'var(--bgInput)', borderRadius:3, padding:'6px 8px', textAlign:'center' }}>
                      <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1 }}>Half Day</div>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--accent)' }}>{fmtKES(r.half_day_rate)}</div>
                    </div>
                    <div style={{ flex:1, background:'var(--bgInput)', borderRadius:3, padding:'6px 8px', textAlign:'center' }}>
                      <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1 }}>Full Day</div>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--accent)' }}>{fmtKES(r.full_day_rate)}</div>
                    </div>
                  </div>
                  {r.amenities && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                      {r.amenities.split(',').map(a=>(
                        <span key={a} style={{ fontSize:9, color:'var(--textMuted)', border:'1px solid #2a2a2a', borderRadius:2, padding:'1px 5px' }}>{a.trim()}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {confRooms.length===0 && <Empty text="No venues configured yet"/>}
            </div>
          </Card>
        </>
      )}

      {/* ── ROOM STATUS ── */}
      {tab==='rooms' && (
        <Card title="Room Status">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:10 }}>
            {rooms.map(r => {
              const g = active.find(g=>g.room===r.id);
              return (
                <div key={r.id} style={{ background:'var(--bgInput)', border:`2px solid ${r.status==='available'?'#2ecc71':'#e67e22'}`, borderRadius:4, padding:14, textAlign:'center' }}>
                  <div style={{ fontSize:18, fontWeight:700, color:'var(--textPrimary)', marginBottom:4 }}>Room {r.number}</div>
                  <div style={{ fontSize:11, color:'var(--textFaint)', marginBottom:4 }}>{r.type} · Fl.{r.floor}</div>
                  <div style={{ color:r.status==='available'?'#2ecc71':'#e67e22', fontSize:12, marginBottom:4 }}>
                    {r.status==='available'?'● Available':'● Occupied'}
                  </div>
                  <div style={{ fontSize:12, color:'var(--accent)' }}>{fmtKES(r.price)}/night</div>
                  {g && <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:4 }}>{g.name}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      )}


      {tab==='transport' && (
        <TransportBookingPanel notify={notify} />
      )}

      {tab==='swimming' && (
        <div style={{ marginTop:-16 }}>
          <SwimmingPage notify={notify} />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
           CHECKOUT MODAL — Full bill preview with folio charges
      ══════════════════════════════════════════════════════════════════════ */}
      {checkoutGuest && checkoutData && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:16, overflowY:'auto' }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:600, width:'100%', overflowX:'hidden', margin:'8px auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
              <div>
                <div style={{ fontSize:11, color:'var(--accent)', letterSpacing:3, textTransform:'uppercase' }}>Checkout</div>
                <div style={{ fontSize:18, fontWeight:700, color:'var(--textPrimary)' }}>{checkoutGuest.name}</div>
                <div style={{ fontSize:12, color:'var(--textFaint)' }}>Room {checkoutGuest.room_number} · {checkoutGuest.nights} night{checkoutGuest.nights>1?'s':''}</div>
              </div>
              <button onClick={()=>setCheckoutGuest(null)} style={{ background:'none', border:'none', color:'var(--textFaint)', fontSize:22, cursor:'pointer' }}>✕</button>
            </div>

            {/* Room charge */}
            <div style={{ background:'var(--bgCard)', borderRadius:4, padding:'10px 14px', marginBottom:10 }}>
              <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:2, textTransform:'uppercase', marginBottom:6 }}>Accommodation</div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                <span style={{ color:'var(--textMuted)' }}>Room {checkoutGuest.room_number} ({checkoutGuest.room_type}) × {checkoutGuest.nights} nights</span>
                <span style={{ color:'var(--textPrimary)', fontWeight:700 }}>{fmtKES(checkoutData.room_charge)}</span>
              </div>
            </div>

            {/* Folio charges grouped by type */}
            {checkoutData.unsettled_charges?.length > 0 && (
              <div style={{ background:'var(--bgCard)', borderRadius:4, padding:'10px 14px', marginBottom:10 }}>
                <div style={{ fontSize:10, color:'#e67e22', letterSpacing:2, textTransform:'uppercase', marginBottom:8 }}>Outstanding Charges</div>
                {/* Group by type */}
                {Object.entries(
                  checkoutData.unsettled_charges.reduce((acc, c) => {
                    const k = c.charge_type||'other';
                    if (!acc[k]) acc[k] = [];
                    acc[k].push(c);
                    return acc;
                  }, {})
                ).map(([type, items]) => (
                  <div key={type} style={{ marginBottom:8 }}>
                    <div style={{ fontSize:10, color:'var(--accent)', textTransform:'uppercase', letterSpacing:1, marginBottom:4, paddingBottom:3, borderBottom:'1px solid var(--border)' }}>
                      {typeLabels[type]||type}
                    </div>
                    {items.map((c,i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'3px 0' }}>
                        <span style={{ color:'var(--textMuted)', flex:1 }}>{c.description}</span>
                        <span style={{ color:'var(--textSecond)', fontWeight:600, marginLeft:12 }}>{fmtKES(c.amount)}</span>
                      </div>
                    ))}
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'4px 0', borderTop:'1px solid var(--border)', marginTop:3 }}>
                      <span style={{ color:'var(--textFaint)' }}>Subtotal</span>
                      <span style={{ color:'var(--textMuted)' }}>{fmtKES(items.reduce((s,c)=>s+parseFloat(c.amount||0),0))}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {checkoutData.unsettled_charges?.length === 0 && (
              <div style={{ padding:'10px 14px', background:'#0a180a', border:'1px solid #2ecc7122', borderRadius:4, marginBottom:10, fontSize:12, color:'#2ecc71' }}>
                ✓ No outstanding folio charges
              </div>
            )}

            {/* Grand total */}
            <div style={{ background:'var(--bgInfo)', border:'1px solid #c8a96e33', borderRadius:4, padding:'12px 14px', marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                <span style={{ color:'var(--textMuted)' }}>Room Charges</span>
                <span style={{ color:'var(--textSecond)' }}>{fmtKES(checkoutData.room_charge)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:8, paddingBottom:8, borderBottom:'1px solid #2a2a2a' }}>
                <span style={{ color:'var(--textMuted)' }}>Folio Charges</span>
                <span style={{ color:'var(--textSecond)' }}>{fmtKES(checkoutData.folio_total)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:18, fontWeight:700 }}>
                <span style={{ color:'var(--textPrimary)' }}>GRAND TOTAL</span>
                <span style={{ color:'var(--accent)' }}>{fmtKES(checkoutData.grand_total)}</span>
              </div>
            </div>

            {/* Payment mode */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10, marginBottom:16 }}>
              <Field label="Payment Mode">
                <select style={inputStyle} value={checkoutMode} onChange={e=>setCheckoutMode(e.target.value)}>
                  <option value="cash">Cash</option>
                  <option value="mpesa">M-Pesa</option>
                  <option value="card">Card / PDQ</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="invoice">Invoice</option>
                  <option value="complimentary">Complimentary</option>
                </select>
              </Field>
              <Field label="Transaction Code (if applicable)">
                <input style={inputStyle} placeholder="M-Pesa, bank ref…" value={checkoutTx} onChange={e=>setCheckoutTx(e.target.value)}/>
              </Field>
            </div>

            {checkoutMode === 'cash' && (
              <div style={{ padding:'8px 12px', background:'#0a180a', border:'1px solid #2ecc7122', borderRadius:3, marginBottom:12, fontSize:12, color:'#2ecc71' }}>
                💵 Cash payment — receipt will be auto-printed after checkout
              </div>
            )}

            {/* Quick Payment Triggers */}
            <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
              <button
                style={{ background:'#00b300', border:'none', borderRadius:3, padding:'8px 14px', color:'#fff', fontSize:12, cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}
                onClick={async () => {
                  const phone = prompt(`Enter guest M-Pesa number (254XXXXXXXXX):\nTotal: KES ${checkoutData?.grand_total?.toLocaleString()}`);
                  if (!phone) return;
                  try {
                    const r = await api.mpesaStkPush({ phone, amount: Math.ceil(checkoutData?.grand_total || 100), description: `Checkout - ${checkoutGuest?.name}` });
                    setCheckoutTx(r.data.CheckoutRequestID || '');
                    setCheckoutMode('mpesa');
                    notify('M-Pesa STK Push sent! Ask guest to enter PIN.');
                  } catch(e) { notify('STK Push failed: ' + (e.response?.data?.error || e.message), 'error'); }
                }}
              >📱 M-Pesa STK Push</button>
              <button
                style={{ background:'#635bff', border:'none', borderRadius:3, padding:'8px 14px', color:'#fff', fontSize:12, cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}
                onClick={async () => {
                  try {
                    const r = await api.stripePayIntent({ amount: Math.ceil((checkoutData?.grand_total || 100) * 100), currency: 'kes', description: `Checkout - ${checkoutGuest?.name}` });
                    setCheckoutTx(r.data.id || '');
                    setCheckoutMode('card');
                    notify('Stripe payment intent created!');
                  } catch(e) { notify('Stripe failed', 'error'); }
                }}
              >💙 Stripe</button>
            </div>

            <div style={{ display:'flex', gap:8 }}>
              <button
                style={{ ...btnPrimary, flex:1, background:'var(--accent)', color:'#111', fontWeight:700 }}
                onClick={confirmCheckout}
                disabled={checkingOut}
              >
                {checkingOut ? 'Processing…' : `✓ Confirm Checkout & Print Invoice`}
              </button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setCheckoutGuest(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
           NEW CONFERENCE BOOKING MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      {confModal && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:16, overflowY:'auto' }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:640, width:'100%', overflowX:'hidden', margin:'8px auto' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'var(--accent)', marginBottom:18 }}>🏛️ New Conference Booking</div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Venue *">
                  <select style={inputStyle} value={confForm.conference_room} onChange={e=>setConfForm(f=>({...f,conference_room:e.target.value}))}>
                    <option value="">— Select Venue —</option>
                    {confRooms.map(r=><option key={r.id} value={r.id}>{r.name} (capacity: {r.capacity})</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Event Name *">
                <input style={inputStyle} placeholder="e.g. Annual AGM 2025" value={confForm.event_name} onChange={e=>setConfForm(f=>({...f,event_name:e.target.value}))}/>
              </Field>
              <Field label="Event Date *">
                <input type="date" style={inputStyle} value={confForm.event_date} onChange={e=>setConfForm(f=>({...f,event_date:e.target.value}))}/>
              </Field>
              <Field label="Session Type">
                <select style={inputStyle} value={confForm.session_type} onChange={e=>setConfForm(f=>({...f,session_type:e.target.value}))}>
                  <option value="half_day_am">Half Day AM</option>
                  <option value="half_day_pm">Half Day PM</option>
                  <option value="full_day">Full Day</option>
                </select>
              </Field>
              <Field label="Number of Attendees">
                <input type="number" min="1" style={inputStyle} value={confForm.attendees} onChange={e=>setConfForm(f=>({...f,attendees:e.target.value}))}/>
              </Field>
              <Field label="Organiser Name *">
                <input style={inputStyle} placeholder="Contact person" value={confForm.organiser_name} onChange={e=>setConfForm(f=>({...f,organiser_name:e.target.value}))}/>
              </Field>
              <Field label="Organisation">
                <input style={inputStyle} placeholder="Company / Organisation" value={confForm.organisation} onChange={e=>setConfForm(f=>({...f,organisation:e.target.value}))}/>
              </Field>
              <Field label="Phone *">
                <input style={inputStyle} placeholder="+254…" value={confForm.organiser_phone} onChange={e=>setConfForm(f=>({...f,organiser_phone:e.target.value}))}/>
              </Field>
              <Field label="Email">
                <input style={inputStyle} placeholder="organiser@email.com" value={confForm.organiser_email} onChange={e=>setConfForm(f=>({...f,organiser_email:e.target.value}))}/>
              </Field>
              <Field label="Payment Mode">
                <select style={inputStyle} value={confForm.payment_mode} onChange={e=>setConfForm(f=>({...f,payment_mode:e.target.value}))}>
                  <option value="cash">Cash</option><option value="mpesa">M-Pesa</option>
                  <option value="bank">Bank Transfer</option><option value="pdq">PDQ/Card</option>
                  <option value="invoice">Invoice</option>
                  <option value="complimentary">Complimentary</option>
                </select>
              </Field>
            </div>

            {/* Extras checkboxes */}
            <div style={{ marginTop:12, marginBottom:12 }}>
              <div style={{ fontSize:10, color:'var(--accent)', letterSpacing:2, textTransform:'uppercase', marginBottom:8 }}>Additional Services</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:6 }}>
                {[
                  ['needs_projector', '📽 Projector (KES 2,000)'],
                  ['needs_microphone','🎤 Microphone/PA (KES 1,500)'],
                  ['needs_catering',  `🍽 Catering (${confForm.attendees} pax × KES 500)`],
                  ['needs_wifi',      '📶 WiFi (included)'],
                ].map(([field, label]) => (
                  <label key={field} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'8px 10px', background:'var(--bgCard)', borderRadius:3, fontSize:12, color:'var(--textMuted)',
                    border:`1px solid ${confForm[field]?'#c8a96e44':'var(--borderMid)'}` }}>
                    <input type="checkbox" checked={!!confForm[field]} onChange={e=>setConfForm(f=>({...f,[field]:e.target.checked}))}
                      style={{ accentColor:'#c8a96e' }}/>
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <Field label="Special Requests">
              <textarea style={{ ...inputStyle, minHeight:60, resize:'vertical' }} placeholder="Any special requirements…" value={confForm.special_requests} onChange={e=>setConfForm(f=>({...f,special_requests:e.target.value}))}/>
            </Field>

            {/* Live cost preview */}
            {selConfRoom && (
              <div style={{ marginTop:10, padding:'12px 14px', background:'#0a1a0a', border:'1px solid #c8a96e33', borderRadius:4 }}>
                <div style={{ fontSize:10, color:'var(--accent)', letterSpacing:2, textTransform:'uppercase', marginBottom:6 }}>Booking Summary</div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:3 }}>
                  <span style={{ color:'var(--textMuted)' }}>{selConfRoom.name} — {SESSION_LABELS[confForm.session_type]}</span>
                  <span style={{ color:'var(--textSecond)' }}>{fmtKES(confRate)}</span>
                </div>
                {confExtras>0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:3 }}>
                    <span style={{ color:'var(--textMuted)' }}>Extras</span>
                    <span style={{ color:'var(--textSecond)' }}>{fmtKES(confExtras)}</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:700, paddingTop:6, borderTop:'1px solid #2a2a2a' }}>
                  <span style={{ color:'var(--textPrimary)' }}>TOTAL</span>
                  <span style={{ color:'var(--accent)' }}>{fmtKES(confTotal)}</span>
                </div>
              </div>
            )}

            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button style={{ ...btnPrimary, flex:1 }} onClick={submitConfBooking}>✓ Confirm Conference Booking</button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setConfModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Conference booking detail modal */}
      {confBookingDetail && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:520, width:'100%', overflowX:'hidden', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:11, color:'var(--accent)', letterSpacing:3, textTransform:'uppercase' }}>Conference Booking</div>
                <div style={{ fontSize:18, fontWeight:700, color:'var(--textPrimary)' }}>{confBookingDetail.confirmation_code}</div>
              </div>
              <button onClick={()=>setConfBookingDetail(null)} style={{ background:'none', border:'none', color:'var(--textFaint)', fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'8px 14px', marginBottom:14 }}>
              {[['Event',confBookingDetail.event_name],['Venue',confBookingDetail.conference_room_name||'—'],
                ['Date',confBookingDetail.event_date],['Session',SESSION_LABELS[confBookingDetail.session_type]||confBookingDetail.session_type],
                ['Attendees',confBookingDetail.attendees],['Organiser',confBookingDetail.organiser_name],
                ['Phone',confBookingDetail.organiser_phone],['Total',fmtKES(confBookingDetail.total)]
              ].map(([l,v])=>(
                <div key={l}><div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1 }}>{l}</div>
                <div style={{ fontSize:13, color:l==='Total'?'#c8a96e':'#ddd', fontWeight:l==='Total'?700:400 }}>{v}</div></div>
              ))}
            </div>
            {(confBookingDetail.status==='confirmed'||confBookingDetail.status==='pending')&&(
              <div style={{ border:'1px solid #2a2a2a', borderRadius:3, padding:12, marginBottom:14 }}>
                <div style={{ fontSize:10, color:'var(--accent)', textTransform:'uppercase', letterSpacing:2, marginBottom:8 }}>Record Payment</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
                  <Field label="Payment Mode">
                    <select style={inputStyle} value={confPayMode} onChange={e=>setConfPayMode(e.target.value)}>
                      <option value="cash">Cash</option><option value="mpesa">M-Pesa</option>
                      <option value="bank">Bank</option><option value="pdq">PDQ</option><option value="invoice">Invoice</option>
                    </select>
                  </Field>
                  <Field label="Tx Code"><input style={inputStyle} value={confTxCode} onChange={e=>setConfTxCode(e.target.value)} placeholder="Optional"/></Field>
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:8 }}>
              <button style={{ ...btnSm }} onClick={()=>printConferenceInvoice(confBookingDetail)}>🖨 Print Invoice</button>
              {(confBookingDetail.status==='confirmed'||confBookingDetail.status==='pending')&&(
                <button style={{ ...btnPrimary, marginTop:0 }} onClick={()=>completeConfPayment(confBookingDetail)}>💰 Record Payment</button>
              )}
              <button style={{ ...btnSm, marginLeft:'auto' }} onClick={()=>setConfBookingDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
